import { zodResolver } from '@hookform/resolvers/zod';
import { Edit2, Mic, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { PageHeader } from '../components/AppShell';
import { ExportMenu } from '../components/ExportMenu';
import { LiveBadge } from '../components/LiveBadge';
import { Topbar } from '../components/Topbar';
import { exportProgrammes } from '../lib/api/export';
import { exportProgrammesPdf } from '../lib/pdf/programmesPdf';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { Switch } from '../components/ui/switch';
import { useToast } from '../components/ui/toast';
import { api } from '../lib/api/client';
import { useAdminProgrammes, useCreateProgramme, useDeleteProgramme } from '../lib/api/programmes';
import { useLocaleStore } from '../lib/locales';
import { slugify } from '../lib/slug';
import type {
  ProgrammeCategory,
  ProgrammeInput,
  ProgrammeMedium,
  PublicProgrammeListItem,
} from '../lib/types';

const slugRe = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

const programmeSchema = z.object({
  /** First-locale title, used to seed the default translation. */
  title: z.string().min(1).max(512),
  slug: z.string().min(1).regex(slugRe),
  defaultLocale: z.string().regex(/^[a-z]{2,8}$/),
  /** Distribution medium — determines which mobile tab surfaces this programme. */
  medium: z.enum(['radio', 'tv', 'both']).default('both'),
  /** Editorial category — drives the Player Programmes-tab filter chips. */
  category: z
    .enum(['news', 'talk', 'music', 'faith', 'cultural', 'other'])
    .default('other'),
  isActive: z.boolean().default(true),
});

/** Stable label/variant pair for the medium badge. */
function mediumPresentation(m: ProgrammeMedium): { label: string; variant: 'brand' | 'outline' | 'success' } {
  switch (m) {
    case 'radio': return { label: 'Radio', variant: 'outline' };
    case 'tv':    return { label: 'TV', variant: 'brand' };
    case 'both':  return { label: 'Simulcast', variant: 'success' };
  }
}

/** Stable label for the category badge. `other` returns null so we don't
 *  render a redundant "Other" pill in the list — only classified rows get a
 *  visible category indicator. */
function categoryLabel(c: ProgrammeCategory): string | null {
  switch (c) {
    case 'news':     return 'News';
    case 'talk':     return 'Talk';
    case 'music':    return 'Music';
    case 'faith':    return 'Faith';
    case 'cultural': return 'Cultural';
    case 'other':    return null;
  }
}

type ProgrammeForm = z.input<typeof programmeSchema>;

export default function Programmes(): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const available = useLocaleStore((s) => s.available);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const list = useAdminProgrammes({ locale, page, pageSize });
  const create = useCreateProgramme();
  const del = useDeleteProgramme();
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<PublicProgrammeListItem | null>(null);
  const { showToast } = useToast();

  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <Topbar title="Programmes" subtitle={`Locale: ${locale.toUpperCase()}`} />
      <PageHeader
        title="Programmes"
        subtitle={`${total.toLocaleString()} programme${total === 1 ? '' : 's'}`}
        actions={
          <div className="flex items-center gap-2">
            <ExportMenu
              options={[
                {
                  format: 'csv',
                  label: 'CSV',
                  description: `One row per programme (${locale.toUpperCase()} title)`,
                  run: () => exportProgrammes({ format: 'csv', locale }),
                },
                {
                  format: 'json',
                  label: 'JSON',
                  description: 'Full programmes incl. translations + slots',
                  run: () => exportProgrammes({ format: 'json', locale }),
                },
                {
                  format: 'pdf',
                  label: 'PDF — Branded report',
                  description: 'All programmes with slots & episodes',
                  run: () => exportProgrammesPdf(locale),
                },
              ]}
            />
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> New programme
            </Button>
          </div>
        }
      />
      <div className="px-6 pb-10">
        {list.isLoading ? (
          <Spinner label="Loading…" />
        ) : Array.isArray(list.data?.items) && list.data.items.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
            <table className="table-base">
              <thead>
                <tr>
                  <th>Programme</th>
                  <th className="w-32">Medium</th>
                  <th className="w-32">Category</th>
                  <th className="w-44">Locales</th>
                  <th className="w-32">Status</th>
                  <th className="w-44">Resolved from</th>
                  <th className="w-32 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((p) => {
                  const m = mediumPresentation(p.medium);
                  return (
                  <tr key={p.id}>
                    <td>
                      <div className="flex items-start gap-3">
                        {p.imageUrl ? (
                          <img
                            src={p.imageUrl}
                            alt=""
                            className="h-10 w-10 shrink-0 rounded object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 shrink-0 rounded bg-surface-muted" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/programmes/${p.slug}`}
                              className="font-medium text-ink hover:text-brand"
                            >
                              {p.title}
                            </Link>
                            <LiveBadge programmeId={p.id} showViewers={false} />
                          </div>
                          <p className="font-mono text-xs text-ink-subtle">{p.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge variant={m.variant}>{m.label}</Badge>
                    </td>
                    <td>
                      {categoryLabel(p.category) ? (
                        <Badge variant="outline">{categoryLabel(p.category)}</Badge>
                      ) : (
                        <span className="text-xs text-ink-subtle">—</span>
                      )}
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {(p.availableLocales ?? []).map((l) => (
                          <Badge
                            key={l}
                            variant={l === p.defaultLocale ? 'brand' : 'outline'}
                            className="uppercase"
                          >
                            {l}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td>
                      <Badge variant={p.isActive ? 'success' : 'neutral'}>
                        {p.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="text-sm text-ink-muted">
                      <Badge variant="outline" className="uppercase">
                        {p.resolvedFromLocale}
                      </Badge>
                    </td>
                    <td className="text-right">
                      <Button asChild variant="ghost" size="icon" aria-label="Edit">
                        <Link to={`/programmes/${p.slug}`}>
                          <Edit2 className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setConfirmDelete(p)}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-danger" />
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-line px-4 py-3 text-sm text-ink-muted">
              <span>
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Empty
            icon={Mic}
            title="No programmes yet"
            description="Create a programme — for example, the morning show or a weekly sport round-up."
            action={<Button onClick={() => setCreating(true)}>New programme</Button>}
          />
        )}
      </div>

      <CreateProgrammeDialog
        open={creating}
        onClose={() => setCreating(false)}
        defaultLocale={locale}
        availableLocales={available.map((l) => l.code)}
        busy={create.isPending}
        onSubmit={async (form) => {
          // Two-step: create the programme row, then seed the default-locale
          // translation so the new programme isn't title-less in lists. If the
          // translation step fails, delete the orphan row so the user can retry
          // cleanly instead of accumulating title-less programmes.
          let createdId: string | null = null;
          try {
            const baseInput: ProgrammeInput = {
              slug: form.slug,
              defaultLocale: form.defaultLocale,
              medium: form.medium,
              category: form.category,
              isActive: form.isActive,
            };
            const result = await create.mutateAsync(baseInput);
            createdId = result.id;
            await api.put(`/admin/programmes/${result.id}/translations/${form.defaultLocale}`, {
              title: form.title,
            });
            showToast({
              title: 'Programme created',
              description: `${form.title} · ${result.slug}`,
              variant: 'success',
            });
            setCreating(false);
          } catch (err) {
            if (createdId) {
              await api.delete(`/admin/programmes/${createdId}`).catch(() => undefined);
            }
            showToast({
              title: 'Could not create',
              description: (err as Error).message,
              variant: 'danger',
            });
          }
        }}
      />

      <Dialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Delete programme?</DialogTitle>
            <DialogDescription>
              Removes <span className="font-mono">{confirmDelete?.slug}</span> and all its
              translations, episodes and slots.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={del.isPending}
              onClick={async () => {
                if (!confirmDelete) return;
                try {
                  await del.mutateAsync(confirmDelete.id);
                  showToast({ title: 'Programme deleted', variant: 'success' });
                  setConfirmDelete(null);
                } catch (err) {
                  showToast({
                    title: 'Delete failed',
                    description: (err as Error).message,
                    variant: 'danger',
                  });
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface CreateProgrammeProps {
  open: boolean;
  onClose: () => void;
  defaultLocale: string;
  availableLocales: string[];
  onSubmit: (form: ProgrammeForm) => void | Promise<void>;
  busy: boolean;
}

function CreateProgrammeDialog({
  open,
  onClose,
  defaultLocale,
  availableLocales,
  onSubmit,
  busy,
}: CreateProgrammeProps): JSX.Element {
  const form = useForm<ProgrammeForm>({
    resolver: zodResolver(programmeSchema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: {
      title: '',
      slug: '',
      defaultLocale,
      medium: 'both',
      category: 'other',
      isActive: true,
    },
  });

  // Auto-derive slug from title until the user manually edits it.
  const watchedTitle = form.watch('title');
  const slugTouched = form.formState.dirtyFields.slug;
  useEffect(() => {
    if (slugTouched) return;
    const candidate = slugify(watchedTitle ?? '');
    if (candidate) form.setValue('slug', candidate, { shouldDirty: false });
  }, [watchedTitle, slugTouched, form]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>New programme</DialogTitle>
          <DialogDescription>
            Sets the default-locale title in one step. Add other locales' translations on the detail
            page.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((values) => {
            const parsed = programmeSchema.parse(values);
            void onSubmit(parsed);
          })}
          className="grid gap-4"
        >
          <div>
            <Label htmlFor="prog-title">Title</Label>
            <Input
              id="prog-title"
              {...form.register('title')}
              placeholder="Morning Show"
              autoFocus
            />
            {form.formState.errors.title ? (
              <p className="mt-1 text-xs text-danger">{form.formState.errors.title.message}</p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="prog-slug">Slug</Label>
            <Input id="prog-slug" {...form.register('slug')} placeholder="auto-from-title" />
            {!slugTouched ? (
              <p className="mt-1 text-xs text-ink-subtle">Auto-generated. Edit to customise.</p>
            ) : null}
            {form.formState.errors.slug ? (
              <p className="mt-1 text-xs text-danger">{form.formState.errors.slug.message}</p>
            ) : null}
          </div>
          <div>
            <Label>Default locale</Label>
            <Select
              value={form.watch('defaultLocale')}
              onValueChange={(v) => form.setValue('defaultLocale', v, { shouldDirty: true })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableLocales.map((l) => (
                  <SelectItem key={l} value={l}>
                    {l.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Medium</Label>
            <Select
              value={form.watch('medium')}
              onValueChange={(v) => form.setValue('medium', v as ProgrammeMedium, { shouldDirty: true })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="radio">Radio only — Listen tab</SelectItem>
                <SelectItem value="tv">TV only — Watch tab</SelectItem>
                <SelectItem value="both">Simulcast — both tabs</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-ink-subtle">
              Determines whether mobile clients surface this programme on Listen, Watch, or both.
            </p>
          </div>
          <div>
            <Label>Category</Label>
            <Select
              value={form.watch('category')}
              onValueChange={(v) => form.setValue('category', v as ProgrammeCategory, { shouldDirty: true })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="news">News</SelectItem>
                <SelectItem value="talk">Talk</SelectItem>
                <SelectItem value="music">Music</SelectItem>
                <SelectItem value="faith">Faith</SelectItem>
                <SelectItem value="cultural">Cultural</SelectItem>
                <SelectItem value="other">Other / unclassified</SelectItem>
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-ink-subtle">
              Drives the Player Programmes-tab filter chips. Leave as Other if you're unsure.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-md border border-line bg-surface-muted px-3 py-2">
            <Label htmlFor="prog-active" className="cursor-pointer">
              Active
            </Label>
            <Switch
              id="prog-active"
              checked={form.watch('isActive')}
              onCheckedChange={(v) => form.setValue('isActive', v, { shouldDirty: true })}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
